import { useState, useEffect, useCallback, useRef } from 'react';
import * as tus from 'tus-js-client';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Sparkles, Plus, Pencil, Trash2, FileSpreadsheet, Loader2, CloudUpload, AlertTriangle, CheckCircle2, Bot, Clapperboard } from 'lucide-react';
import { toast } from 'sonner';
import { CoachLaunchToggle } from '../../components/admin/CoachLaunchToggle';

const EMPTY_FORM = {
  title: '', description: '', admin_code: '', intensity: 'gentle', level: 'all',
  focus_area: '', tags: [], avoid_if: [], safe_for_everyone: false,
  min_week: '', max_week: '', recommended_frequency: '1', status: 'draft', stage: 'prenatal',
};

const STATUS_CLS = {
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  draft: 'bg-slate-50 text-slate-700 border-slate-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

export default function PrenatalVideosAdmin() {
  const [videos, setVideos] = useState([]);
  const [config, setConfig] = useState(null);
  const [introId, setIntroId] = useState(null);
  const setIntro = async (v) => {
    const id = v ? (v._id || v.id) : null;
    try {
      await api.put('/api/admin/prenatal-coach/intro', { video_id: id });
      setIntroId(id); toast.success(id ? `"${v.title}" is now the SoulAI intro video` : 'Intro video cleared');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set intro'); }
  };
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [sheetUrl, setSheetUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  const fetchVideos = useCallback(() => {
    api.get('/api/admin/prenatal-coach/videos')
      .then((r) => { setVideos(r.data.videos || []); setConfig(r.data.config); setIntroId(r.data.intro_video_id || null); })
      .catch(() => toast.error('Failed to load videos'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      title: v.title || '', description: v.description || '', admin_code: v.admin_code || '',
      intensity: v.intensity || 'gentle', level: v.level || 'all', focus_area: v.focus_area || '',
      tags: v.tags || [], avoid_if: v.avoid_if || [], safe_for_everyone: !!v.safe_for_everyone,
      min_week: v.min_week ?? '', max_week: v.max_week ?? '',
      recommended_frequency: String(v.recommended_frequency || 1), status: v.status || 'draft',
      stage: v.stage || 'prenatal',
    });
    setFormOpen(true);
  };

  const toggleIn = (key, val) => setForm((f) => ({
    ...f, [key]: f[key].includes(val) ? f[key].filter((s) => s !== val) : [...f[key], val],
  }));

  const saveVideo = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    const payload = {
      title: form.title, description: form.description,
      admin_code: form.admin_code.trim() || null,
      intensity: form.intensity, level: form.level,
      focus_area: form.focus_area || null, tags: form.tags,
      avoid_if: form.avoid_if, safe_for_everyone: form.safe_for_everyone,
      min_week: form.min_week ? parseInt(form.min_week, 10) : null,
      max_week: form.max_week ? parseInt(form.max_week, 10) : null,
      recommended_frequency: parseInt(form.recommended_frequency, 10) || 1,
      status: form.status, stage: form.stage,
    };
    try {
      if (editing) await api.put(`/api/admin/prenatal-coach/videos/${editing._id || editing.id}`, payload);
      else await api.post('/api/admin/prenatal-coach/videos', payload);
      toast.success(editing ? 'Video updated' : 'Video created');
      setFormOpen(false); fetchVideos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? This also removes its Bunny video.`)) return;
    try {
      await api.delete(`/api/admin/prenatal-coach/videos/${v._id || v.id}`);
      toast.success('Deleted'); fetchVideos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const startUpload = async (v) => {
    const id = v._id || v.id;
    try {
      const res = await api.post(`/api/admin/prenatal-coach/videos/${id}/create-upload`);
      fileInputRefs.current[id].dataset.tus = JSON.stringify(res.data.tus);
      fileInputRefs.current[id].click();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to prepare upload'); }
  };

  const uploadFile = async (v, file) => {
    const id = v._id || v.id;
    const input = fileInputRefs.current[id];
    const tusCfg = JSON.parse(input.dataset.tus || '{}');
    if (!tusCfg.endpoint) { toast.error('Upload not prepared — please try again'); return; }
    setUploading((p) => ({ ...p, [id]: 0 }));
    try {
      await new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: tusCfg.endpoint,
          headers: tusCfg.headers,
          // Resumable + auto-retry survives flaky networks / large files.
          retryDelays: [0, 3000, 5000, 10000, 20000, 30000],
          chunkSize: 50 * 1024 * 1024,
          removeFingerprintOnSuccess: true,
          metadata: { filetype: file.type || 'video/mp4', title: tusCfg.title || file.name },
          onError: (error) => reject(error),
          onProgress: (sent, total) => setUploading((p) => ({ ...p, [id]: Math.round((sent / total) * 100) })),
          onSuccess: () => resolve(),
        });
        upload.start();
      });
      await api.post(`/api/admin/prenatal-coach/videos/${id}/confirm-upload`);
      toast.success('Video uploaded — duration will auto-detect after processing');
      fetchVideos();
    } catch (err) { toast.error(`Upload failed: ${err.message || err}`); }
    setUploading((p) => { const n = { ...p }; delete n[id]; return n; });
  };

  const runSheetImport = async () => {
    if (!sheetUrl.trim()) return toast.error('Paste your Google Sheet link');
    setImporting(true); setImportResult(null);
    try {
      const res = await api.post('/api/admin/prenatal-coach/videos/import-sheet', { sheet_url: sheetUrl.trim() });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.created} videos as drafts`);
      fetchVideos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full rounded-2xl" /></div>;

  return (
    <div className="space-y-4" data-testid="prenatal-videos-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />Prenatal Coach — Video Library</h1>
          <p className="text-sm text-muted-foreground">Import from your Google Sheet, review AI mapping, upload the video file, then activate.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setImportResult(null); setImportOpen(true); }} data-testid="import-sheet-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Import from Google Sheet</Button>
          <Button onClick={openCreate} data-testid="add-video-btn"><Plus className="h-4 w-4 mr-1.5" />Add Video</Button>
        </div>
      </div>

      <CoachLaunchToggle />

      <Card className="rounded-2xl">
        <CardContent className="pt-4 overflow-x-auto">
          {videos.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">No videos yet — import your library from the Google Sheet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Intensity</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Avoid-If</TableHead>
                  <TableHead>Weeks</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Video</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {videos.map((v) => {
                  const id = v._id || v.id;
                  return (
                    <TableRow key={id} data-testid={`video-row-${id}`}>
                      <TableCell className="text-xs text-muted-foreground">{v.order}</TableCell>
                      <TableCell>
                        {v.admin_code ? <Badge variant="outline" className="font-mono text-[10px]" data-testid={`video-code-${id}`}>{v.admin_code}</Badge> : <span className="text-[11px] text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{v.title}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {v.duration_minutes ? `${v.duration_minutes} min` : (v.upload_status === 'ready' ? 'detecting duration…' : 'duration auto-detects')}
                          {v.focus_area ? ` • ${v.focus_area}` : ''}
                          {v.recommended_frequency > 1 ? ` • up to ${v.recommended_frequency}×/wk` : ''}
                          {v.safe_for_everyone ? ' • ✓ safe-for-everyone' : ''}
                        </p>
                      </TableCell>
                      <TableCell><Badge className={`text-[10px] ${v.intensity === 'moderate' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>{v.intensity}</Badge></TableCell>
                      <TableCell className="text-xs capitalize">{v.level}</TableCell>
                      <TableCell className="max-w-[200px]">
                        <div className="flex flex-wrap gap-1">
                          {(v.avoid_if || []).length === 0 ? <span className="text-[11px] text-muted-foreground">—</span> :
                            v.avoid_if.map((s) => <Badge key={s} variant="outline" className="text-[9px]">{s.replace(/_/g, ' ')}</Badge>)}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{v.min_week || v.max_week ? `${v.min_week || 1}–${v.max_week || 42}` : 'All'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={`text-[10px] w-fit ${STATUS_CLS[v.status] || ''}`}>{v.status}</Badge>
                          {v.stage && v.stage !== 'prenatal' && <Badge variant="outline" className="text-[9px] w-fit border-violet-200 text-violet-800 bg-violet-50" data-testid={`stage-${id}`}>{v.stage}</Badge>}
                          {v.needs_review && <Badge className="text-[9px] w-fit bg-amber-50 text-amber-800 border-amber-300" data-testid={`needs-review-${id}`}><AlertTriangle className="h-2.5 w-2.5 mr-0.5" />review</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {uploading[id] !== undefined ? (
                          <span className="text-xs text-primary flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{uploading[id]}%</span>
                        ) : v.upload_status === 'ready' ? (
                          <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">Uploaded</Badge>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => startUpload(v)} data-testid={`upload-video-${id}`}>
                            <CloudUpload className="h-3.5 w-3.5 mr-1" />{v.upload_status === 'awaiting_upload' ? 'Resume' : 'Upload'}
                          </Button>
                        )}
                        <input type="file" accept="video/*" className="hidden" ref={(el) => { if (el) fileInputRefs.current[id] = el; }}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(v, f); e.target.value = ''; }} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className={`h-7 px-2 ${introId === id ? 'text-primary' : 'text-muted-foreground'}`} title={introId === id ? 'This is the SoulAI intro video (click to clear)' : 'Set as SoulAI intro video'}
                          onClick={() => setIntro(introId === id ? null : v)} data-testid={`set-intro-${id}`}>
                          <Clapperboard className="h-3.5 w-3.5" />{introId === id && <span className="ml-1 text-[10px] font-semibold">INTRO</span>}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEdit(v)} data-testid={`edit-video-${id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => deleteVideo(v)} data-testid={`delete-video-${id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="video-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit video' : 'Add video'}</DialogTitle>
            <DialogDescription className="text-xs">Metadata drives the safety engine — tag Avoid-If conditions carefully. Duration auto-detects after upload.</DialogDescription>
          </DialogHeader>
          {editing?.source?.mapping_notes && (
            <div className="rounded-lg bg-sky-50 border border-sky-200 p-2.5 text-[11px] text-sky-900 flex gap-2" data-testid="ai-mapping-notes">
              <Bot className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">AI mapping notes</p>
                <p>{editing.source.mapping_notes}</p>
                {editing.source.avoid_if_raw && <p className="mt-1 text-sky-700">Sheet Avoid-If: “{editing.source.avoid_if_raw}”</p>}
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input placeholder="Title * (learner-facing)" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="video-form-title" />
              </div>
              <Input placeholder="Code (T1S4)" className="font-mono" value={form.admin_code} onChange={(e) => setForm((f) => ({ ...f, admin_code: e.target.value }))} data-testid="video-form-code" />
            </div>
            <Textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="video-form-description" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Intensity</p>
                <Select value={form.intensity} onValueChange={(val) => setForm((f) => ({ ...f, intensity: val }))}>
                  <SelectTrigger data-testid="video-form-intensity"><SelectValue /></SelectTrigger>
                  <SelectContent>{(config?.intensities || []).map((i) => <SelectItem key={i} value={i} className="capitalize">{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Level</p>
                <Select value={form.level} onValueChange={(val) => setForm((f) => ({ ...f, level: val }))}>
                  <SelectTrigger data-testid="video-form-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{(config?.levels || []).map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Focus area</p>
                <Select value={form.focus_area || undefined} onValueChange={(val) => setForm((f) => ({ ...f, focus_area: val }))}>
                  <SelectTrigger data-testid="video-form-focus"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{(config?.focus_areas || []).map((fa) => <SelectItem key={fa} value={fa}>{fa}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Max repeats / week</p>
                <Select value={form.recommended_frequency} onValueChange={(val) => setForm((f) => ({ ...f, recommended_frequency: val }))}>
                  <SelectTrigger data-testid="video-form-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{(config?.frequencies || [1, 2, 3]).map((n) => <SelectItem key={n} value={String(n)}>{n}× per week</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Min week (strict)</p>
                <Input type="number" min="1" max="42" value={form.min_week} onChange={(e) => setForm((f) => ({ ...f, min_week: e.target.value }))} data-testid="video-form-minweek" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Max week (soft)</p>
                <Input type="number" min="1" max="42" value={form.max_week} onChange={(e) => setForm((f) => ({ ...f, max_week: e.target.value }))} data-testid="video-form-maxweek" />
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Tags (secondary goals)</p>
              <div className="flex flex-wrap gap-1.5">
                {(config?.tags || []).map((t) => (
                  <button key={t} type="button" onClick={() => toggleIn('tags', t)} data-testid={`tag-${t.replace(/[^a-zA-Z]/g, '')}`}
                    className={`px-2 py-1 rounded-full border text-[10px] font-medium transition-colors ${form.tags.includes(t) ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Avoid-If — users with these conditions will NEVER see this video</p>
              <div className="grid grid-cols-2 gap-1.5">
                {(config?.conditions || []).filter((c) => form.stage === 'both' || (form.stage === 'postnatal' ? (config?.postnatal_conditions || []).some((x) => x.slug === c.slug) || c.implicit : (config?.prenatal_conditions || config?.conditions || []).some((x) => x.slug === c.slug))).map((c) => (
                  <button key={c.slug} type="button" onClick={() => toggleIn('avoid_if', c.slug)} data-testid={`avoidif-${c.slug}`}
                    className={`text-left px-2 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${form.avoid_if.includes(c.slug) ? 'border-rose-400 bg-rose-50 text-rose-800' : 'border-border hover:bg-muted'}`}>
                    {c.label}{c.severity === 'hard' && <span className="text-rose-500"> •</span>}{c.implicit && <span className="text-sky-600"> (auto)</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setForm((f) => ({ ...f, safe_for_everyone: !f.safe_for_everyone }))} data-testid="video-form-safe"
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.safe_for_everyone ? 'bg-emerald-50 border-emerald-400 text-emerald-800' : 'border-border'}`}>
                ✓ Safe for everyone (used in doctor-first preset)
              </button>
              <Select value={form.status} onValueChange={(val) => setForm((f) => ({ ...f, status: val }))}>
                <SelectTrigger className="w-32" data-testid="video-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving} data-testid="video-form-cancel">Cancel</Button>
            <Button onClick={saveVideo} disabled={saving} data-testid="video-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Save changes' : 'Create video'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Google Sheet import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="sheet-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Import from Google Sheet</DialogTitle>
            <DialogDescription className="text-xs">
              Every tab (T1S1, T1S2…) becomes one video. AI maps Avoid-If, focus area, title & description —
              everything lands as <b>Draft + needs review</b>. The sheet must be shared as
              “Anyone with the link — Viewer”.
            </DialogDescription>
          </DialogHeader>
          <Input placeholder="https://docs.google.com/spreadsheets/d/…" value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)} data-testid="sheet-url-input" />
          {importing && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2" data-testid="sheet-import-progress">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />Reading tabs & mapping with AI — this can take a minute…
            </div>
          )}
          {importResult && (
            <div className="space-y-2 text-xs" data-testid="sheet-import-result">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5 text-emerald-900 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /><b>{importResult.created}</b> videos imported as drafts — review Avoid-If tags, upload files, then activate.
              </div>
              {importResult.videos?.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-lg border divide-y">
                  {importResult.videos.map((v) => (
                    <div key={v.code} className="px-2.5 py-1.5 flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-[9px]">{v.code}</Badge>
                      <span className="font-medium">{v.title}</span>
                      <span className="text-muted-foreground">{v.focus_area || ''}</span>
                      {!v.ai_mapped && <Badge className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">AI mapping failed — map manually</Badge>}
                    </div>
                  ))}
                </div>
              )}
              {importResult.skipped_existing?.length > 0 && (
                <p className="text-muted-foreground">Skipped (already imported): {importResult.skipped_existing.join(', ')}</p>
              )}
              {importResult.skipped_tabs?.length > 0 && (
                <p className="text-muted-foreground">Non-video tabs ignored: {importResult.skipped_tabs.join(', ')}</p>
              )}
              {importResult.errors?.length > 0 && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-2.5 text-rose-800">
                  {importResult.errors.map((e) => <p key={e.tab}>{e.tab}: {e.error}</p>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {importResult ? (
              <Button variant="outline" onClick={() => setImportOpen(false)} data-testid="sheet-import-done">Done</Button>
            ) : (
              <Button onClick={runSheetImport} disabled={importing} data-testid="sheet-import-run">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Import & map with AI
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
